import axios from 'axios';
import { ConversationModel, Resource } from '@/shared/model/conversation.model';
import { AskMessageInfoRequest, HistoryChatResponse, Language, MergeChatObject } from '@/shared/model/ask-goover.model';

const ASK_GOOVER_API = 'api/ask-goover';

export default class AskGooverService {
  constructor() {}

  chat(
    conversationId: string,
    userKey: string,
    language: string,
    message: string,
    page: string,
    resourceIds: Array<Resource>,
    briefingId?: string,
    newChat?: boolean
  ) {
    const requestBody = {
      conversationId: conversationId,
      userKey: userKey,
      language: language.toUpperCase(),
      message: message,
      page: page,
      resourceIds: resourceIds,
      briefingId: briefingId || '',
      newChat: newChat || undefined,
    };

    return new Promise<any>((resolve, reject) => {
      axios.post(ASK_GOOVER_API + '/ask-manager/chat', requestBody).then(res => resolve(res));
    });
  }

  generateConversationId(userKey, language, page, briefingId?, docId?, conversationId?, requestId?, stockCode?) {
    let URL = ASK_GOOVER_API;
    const requestBody = {
      userKey: userKey,
      language: language.toUpperCase(),
      page: page,
    };

    requestBody['stockCode'] = stockCode || '';
    requestBody['briefingId'] = briefingId || '';
    requestBody['docId'] = docId || '';
    requestBody['conversationId'] = conversationId || '';
    URL = URL + '/ask-manager';

    if (requestId) {
      requestBody['requestId'] = requestId;
    }

    return new Promise<any>((resolve, reject) => {
      axios
        .post(URL + '/chat/new', requestBody)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  generateInDepthAnswer(messageId: string) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + '/ask-manager/chat/metasearch', { messageId: messageId })
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  getSuggestQuestions(suggestRequest: ConversationModel) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + '/ask-manager/suggest', suggestRequest)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  saveLogChat(suggestRequest: any) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + '/ask-manager/logChat', suggestRequest)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  getAllConversations(userId) {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(ASK_GOOVER_API + '/conversation/all?userId=' + userId)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  getConversationById(conversationId) {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(ASK_GOOVER_API + '/ask-manager/history?conversationId=' + conversationId)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  getChatHistoryInHome(userKey: string, language: Language = Language.EN, pageNo = 0) {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(ASK_GOOVER_API + `/ask-manager/history/home?userKey=${userKey}&language=${language.toUpperCase()}&pageNo=${pageNo}`)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  deleteChatHistory(conversationId: string) {
    if (!conversationId) return;
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + `/delete/conversation`, { conversationId })
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  deleteConversation(conversationId, userId) {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(ASK_GOOVER_API + `/conversation?conversationId=${conversationId}&userId=${userId}`)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  summarize(conversationId, resourceId, language) {
    const requestBody = {
      conversationId: conversationId,
      resourceId: resourceId,
      language: language,
    };

    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + `/summarize`, requestBody)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  summaryAndSuggestQuestions(conversationId, resourceId, language) {
    const requestBody = {
      conversationId: conversationId,
      language: language,
      resourceId: resourceId,
    };

    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + `/suggest-summary`, requestBody)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  generateMeta(conversationId, title, summary, tags) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(
          ASK_GOOVER_API + `/conversation/generate-meta?conversationId=${conversationId}&title=${title}&summary=${summary}&tags=${tags}`
        )
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  getAllUserFavoriteMessage(userId) {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(ASK_GOOVER_API + '/message-favorite/' + userId)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  getCompanyWelcome(requestBody) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + '/company/welcome', requestBody)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  getCompanySummarize(requestBody) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + '/company/summarize', requestBody)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  saveUserFavoriteMessage(message) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + '/message-favorite/save', message)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  userInteraction(messageInfoRequest: AskMessageInfoRequest) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + '/message/info', messageInfoRequest)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  deleteUserFavoriteMessage(messageId) {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(ASK_GOOVER_API + '/message-favorite/delete?messageId=' + messageId)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  mergeChat(dataInput: MergeChatObject) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(ASK_GOOVER_API + '/history/copy', dataInput)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  getHistoryCreate(userKey, language, pageNo) {
    return new Promise((resolve, reject) => {
      axios
        .get(`${ASK_GOOVER_API}/ask-manager/history/mycreate`, {
          params: {
            userKey,
            language,
            pageNo,
            pageSize: 20,
          },
        })
        .then(res => resolve(res.data as HistoryChatResponse))
        .catch(err => reject(err));
    });
  }

  addBookMark(payload: any) {
    return new Promise((resolve, reject) => {
      axios
        .post(`${ASK_GOOVER_API}/ask-manager/history/mycreate/bookmark`, payload)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  saveConversationTitle(payload: any) {
    return new Promise((resolve, reject) => {
      axios
        .post(`${ASK_GOOVER_API}/title`, payload)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }
}
